import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLists } from '../contexts/ListsContext';
import { useUserDesignations } from '../contexts/UserDesignationsContext';
import { useNotificationModal } from '../contexts/NotificationModalContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { usePlaceAutocomplete } from '../hooks/usePlaceAutocomplete';
import { SelectedPlaceCard, DesignationSelect, AvoidReasonSelect } from '../components/AddPlaceModal/index';
import LimitReachedModal from '../components/LimitReachedModal';
import api from '../services/api';
import '../styles/AddPlaceFromSearch.css';

const INITIAL_FORM_DATA = {
  name: '',
  placeType: '',
  category: '',
  address: '',
  lat: null,
  lng: null,
  rating: 5,
  notes: '',
  designation: '',
  avoidReason: '',
  addressComponents: null,
  placeId: null,
  priceLevel: null,
  photoReference: null,
  googleMapsUrl: null,
  openingHours: null,
  businessStatus: null,
};

// Social links for users to link to their own content about this place
const SOCIAL_LINK_TYPES = [
  { value: 'tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/@...', icon: 'tiktok' },
  { value: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/...', icon: 'youtube' },
  { value: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/...', icon: 'instagram' },
  { value: 'website', label: 'Website/Blog', placeholder: 'https://...', icon: 'globe' },
];

// Validate social link URL format and domain
const validateSocialLink = (type, url) => {
  if (!url || !url.trim()) return { valid: true }; // Empty is valid (optional)

  const trimmedUrl = url.trim();

  // Check valid URL format
  try {
    const parsed = new URL(trimmedUrl);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return { valid: false, error: 'URL must start with http:// or https://' };
    }
  } catch {
    return { valid: false, error: 'Invalid URL format' };
  }

  // Check blocked domains (URL shorteners, etc.)
  const blockedPatterns = [/bit\.ly/i, /tinyurl\.com/i, /t\.co/i, /goo\.gl/i];
  if (blockedPatterns.some(p => p.test(trimmedUrl))) {
    return { valid: false, error: 'URL shorteners are not allowed' };
  }

  // Check domain for specific platforms
  const domainPatterns = {
    instagram: [/instagram\.com/i, /instagr\.am/i],
    tiktok: [/tiktok\.com/i],
    youtube: [/youtube\.com/i, /youtu\.be/i],
    website: null, // Any domain allowed
  };

  const patterns = domainPatterns[type];
  if (patterns !== null && !patterns.some(p => p.test(trimmedUrl))) {
    const typeLabel = SOCIAL_LINK_TYPES.find(t => t.value === type)?.label || type;
    return { valid: false, error: `URL must be a valid ${typeLabel} link` };
  }

  return { valid: true };
};

function AddPlaceFromSearch() {
  const navigate = useNavigate();
  const { lists, addPlaceToMultipleLists } = useLists();
  const { getDesignation, setDesignation: saveDesignation, ensureDesignationsLoaded } = useUserDesignations();
  const { showToast } = useNotificationModal();
  const { isPremium } = useSubscription();

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [placeSelected, setPlaceSelected] = useState(false);
  const [existingPlaceInfo, setExistingPlaceInfo] = useState(null);
  const [autocompleteKey, setAutocompleteKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [limitModalOpen, setLimitModalOpen] = useState(false);
  const [limitModalLimit, setLimitModalLimit] = useState(30);
  const [selectedLists, setSelectedLists] = useState({});
  const [error, setError] = useState(null);
  const [isPrivate, setIsPrivate] = useState(false);
  const [socialLinks, setSocialLinks] = useState({});
  const [socialLinkErrors, setSocialLinkErrors] = useState({});

  // Ensure designations are loaded for duplicate checking
  useEffect(() => {
    ensureDesignationsLoaded();
  }, [ensureDesignationsLoaded]);

  // Check if place exists on any lists
  const checkPlaceExistence = useCallback((googlePlaceId) => {
    setExistingPlaceInfo(null);

    if (!googlePlaceId || !lists || lists.length === 0) return;

    // Check all lists for existing place
    const listsWithPlace = lists
      .filter(list => list.items && list.items.some(item => item.placeId === googlePlaceId))
      .map(list => ({
        listId: list.id,
        listName: list.title,
      }));

    // Check if user already has a designation for this place (global)
    const existingDesignation = getDesignation(googlePlaceId);

    if (existingDesignation || listsWithPlace.length > 0) {
      setExistingPlaceInfo({
        lists: listsWithPlace,
        badge: existingDesignation?.badge,
        avoidReason: existingDesignation?.avoidReason
      });
      // Pre-fill the designation from existing user designation
      if (existingDesignation) {
        setFormData(prev => ({
          ...prev,
          designation: existingDesignation.badge || '',
          avoidReason: existingDesignation.avoidReason || '',
          notes: existingDesignation.notes || '',
        }));
        setIsPrivate(existingDesignation.isPrivate || false);
        // Pre-fill social links if they exist
        if (existingDesignation.socialLinks) {
          setSocialLinks(existingDesignation.socialLinks);
        }
      }
      // Pre-select lists that already have this place
      const preSelected = {};
      listsWithPlace.forEach(l => {
        preSelected[l.listId] = true;
      });
      setSelectedLists(preSelected);
    }
  }, [lists, getDesignation]);

  // Handle place selection from autocomplete
  const handlePlaceSelected = useCallback((placeData) => {
    setFormData((prevData) => ({
      ...prevData,
      ...placeData,
    }));
    setPlaceSelected(true);
    setError(null);
    checkPlaceExistence(placeData.placeId);
  }, [checkPlaceExistence]);

  // Use autocomplete hook
  const {
    containerRef,
    clearInput,
    showDropdown,
    focusInput,
  } = usePlaceAutocomplete({
    isOpen: true,
    autocompleteKey,
    onPlaceSelected: handlePlaceSelected,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDesignationChange = (e) => {
    const designation = e.target.value;
    setFormData({
      ...formData,
      designation,
      avoidReason: designation === 'avoid' ? formData.avoidReason : ''
    });
  };

  const handleListToggle = (listId) => {
    setSelectedLists(prev => ({
      ...prev,
      [listId]: !prev[listId]
    }));
  };

  const handleSocialLinkChange = (type, value) => {
    setSocialLinks(prev => ({
      ...prev,
      [type]: value
    }));
    // Validate on change
    const validation = validateSocialLink(type, value);
    setSocialLinkErrors(prev => ({
      ...prev,
      [type]: validation.valid ? null : validation.error
    }));
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM_DATA);
    setPlaceSelected(false);
    setExistingPlaceInfo(null);
    setSelectedLists({});
    setError(null);
    setIsPrivate(false);
    setSocialLinks({});
    setSocialLinkErrors({});
    clearInput();
    setAutocompleteKey(prev => prev + 1);
  };

  const handleClearPlace = () => {
    resetForm();
    showDropdown();
    focusInput();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!placeSelected || !formData.name || !formData.address) {
      setError('Please select a place from the search');
      return;
    }

    if (!formData.designation) {
      setError('Please select a designation');
      return;
    }

    if (formData.designation === 'avoid' && !formData.avoidReason) {
      setError('Please select a reason to avoid');
      return;
    }

    // Check if at least one list is selected
    const hasSelectedList = Object.values(selectedLists).some(v => v);
    if (!hasSelectedList) {
      setError('Please select at least one list');
      return;
    }

    // Validate all social links before submit
    const linkErrors = {};
    let hasLinkError = false;
    Object.entries(socialLinks).forEach(([type, url]) => {
      if (url) {
        const validation = validateSocialLink(type, url);
        if (!validation.valid) {
          linkErrors[type] = validation.error;
          hasLinkError = true;
        }
      }
    });
    if (hasLinkError) {
      setSocialLinkErrors(linkErrors);
      setError('Please fix the social link errors');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const existingListIds = existingPlaceInfo?.lists?.map(info => info.listId) || [];

      // Filter out empty social links
      const filteredSocialLinks = Object.fromEntries(
        Object.entries(socialLinks).filter(([_, url]) => url && url.trim())
      );

      // Save the user's designation (global, per-user per-place)
      await saveDesignation(formData.placeId, {
        badge: formData.designation,
        avoidReason: formData.designation === 'avoid' ? formData.avoidReason : null,
        notes: formData.notes || null,
        isPrivate,
        socialLinks: Object.keys(filteredSocialLinks).length > 0 ? filteredSocialLinks : null,
      });

      const now = new Date().toISOString();

      // Collect lists to add to (new selections only)
      const listsToAdd = lists
        .filter(list => selectedLists[list.id] && !existingListIds.includes(list.id))
        .map(list => list.id);

      if (listsToAdd.length > 0) {
        // Create the place object (no designation data - that's stored separately)
        const newPlace = {
          id: Date.now() + Math.random(),
          name: formData.name,
          placeType: formData.placeType || null,
          category: formData.category || 'Activities',
          address: formData.address,
          lat: formData.lat || 0,
          lng: formData.lng || 0,
          addressComponents: formData.addressComponents,
          placeId: formData.placeId || null,
          priceLevel: formData.priceLevel !== null ? formData.priceLevel : null,
          photoUrl: formData.photoReference || null,
          photoUrlSavedAt: formData.photoReference ? now : null,
          googleMapsUrl: formData.googleMapsUrl || null,
          openingHours: formData.openingHours || null,
          openingHoursSavedAt: formData.openingHours ? now : null,
          businessStatus: formData.businessStatus || null,
          savedAt: now,
        };

        // Use batch endpoint to add to all lists at once
        await addPlaceToMultipleLists(newPlace, listsToAdd);

        // For premium users, enrich the place (fire-and-forget)
        if (isPremium && formData.placeId) {
          api.enrichPlace(formData.placeId).catch((error) => {
            console.error('Failed to enrich place (non-blocking):', error);
          });
        }
      }

      showToast('Place saved successfully!');
      navigate(-1);
    } catch (err) {
      console.error('Failed to save place:', err);
      if (err.response?.data?.code === 'PLACE_LIMIT_REACHED' ||
          err.message?.includes('limit') ||
          err.code === 'PLACE_LIMIT_REACHED') {
        const limit = err.response?.data?.limit || err.limit || 30;
        setLimitModalLimit(limit);
        setLimitModalOpen(true);
      } else {
        setError(err.message || 'Failed to save place');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    navigate(-1);
  };

  return (
    <div className="add-place-search-page">
      <div className="add-place-search-content">
        <div className="form-header">
          <button className="back-btn" onMouseDown={handleClose} aria-label="Go back">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <h1 className="form-title">Add Place</h1>
          <div className="header-spacer" />
        </div>

        <form onSubmit={handleSubmit} className="add-place-search-form">
          {!placeSelected && (
            <div className="form-group">
              <label>Search for a place</label>
              <div ref={containerRef} className="place-autocomplete-container" />
            </div>
          )}

          {placeSelected && (
            <>
              <SelectedPlaceCard
                formData={formData}
                duplicateError={null}
                existingPlaceInfo={existingPlaceInfo}
                onClear={handleClearPlace}
              />

              <DesignationSelect
                designation={formData.designation}
                onChange={handleDesignationChange}
                existingPlaceInfo={existingPlaceInfo}
                duplicateError={null}
              />

              {formData.designation === 'avoid' && (
                <AvoidReasonSelect
                  avoidReason={formData.avoidReason}
                  onChange={handleInputChange}
                />
              )}

              {/* 3. Notes */}
              <div className="form-group">
                <label>Notes <span className="optional-label">(optional)</span></label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Add your thoughts, tips, or recommendations..."
                  className="form-textarea"
                  rows="3"
                />
              </div>

              {/* 4. Social Links */}
              <div className="form-group social-links-group">
                <label>Social Links <span className="optional-label">(optional)</span></label>
                <p className="form-hint">Link to your content about this place</p>
                <div className="social-links-list">
                  {SOCIAL_LINK_TYPES.map(({ value: type, label, placeholder, icon }) => (
                    <div key={type} className={`social-link-input ${socialLinkErrors[type] ? 'has-error' : ''}`}>
                      <div className="social-link-icon">
                        {icon === 'instagram' && (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                            <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                            <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                          </svg>
                        )}
                        {icon === 'tiktok' && (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                          </svg>
                        )}
                        {icon === 'youtube' && (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"/>
                            <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"/>
                          </svg>
                        )}
                        {icon === 'globe' && (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="2" y1="12" x2="22" y2="12"/>
                            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                          </svg>
                        )}
                      </div>
                      <input
                        type="url"
                        className="form-input social-link-url"
                        value={socialLinks[type] || ''}
                        onChange={(e) => handleSocialLinkChange(type, e.target.value)}
                        placeholder={placeholder}
                      />
                      {socialLinks[type] && (
                        <button
                          type="button"
                          className="social-link-clear"
                          onClick={() => handleSocialLinkChange(type, '')}
                          aria-label={`Clear ${label}`}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <line x1="18" y1="6" x2="6" y2="18"/>
                            <line x1="6" y1="6" x2="18" y2="18"/>
                          </svg>
                        </button>
                      )}
                      {socialLinkErrors[type] && (
                        <div className="social-link-error">{socialLinkErrors[type]}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* 5. Privacy toggle */}
              <div className="form-group privacy-toggle-group">
                <div className="privacy-toggle-row">
                  <div className="privacy-toggle-icon">
                    {isPrivate ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="2" y1="12" x2="22" y2="12"/>
                        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                      </svg>
                    )}
                  </div>
                  <span className="privacy-toggle-label-text">{isPrivate ? 'Private' : 'Public'}</span>
                  <label className="privacy-toggle-switch">
                    <input
                      type="checkbox"
                      checked={isPrivate}
                      onChange={(e) => setIsPrivate(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                <p className="privacy-toggle-description">
                  {isPrivate
                    ? 'Your designation, notes, and social links won\'t be visible to other users'
                    : 'Your designation, notes, and social links can be seen by other users'
                  }
                </p>
              </div>

              {/* 6. List selection with checkboxes */}
              {lists.length > 0 ? (
                <div className="form-group">
                  <label>Add to Lists <span className="required-indicator">*</span></label>
                  <div className="lists-checklist">
                    {[...lists].sort((a, b) => a.title.localeCompare(b.title)).map(list => {
                      const alreadyOnList = existingPlaceInfo?.lists?.some(l => l.listId === list.id);
                      return (
                        <label key={list.id} className={`list-checkbox-label ${alreadyOnList ? 'already-on-list' : ''}`}>
                          <input
                            type="checkbox"
                            checked={selectedLists[list.id] || false}
                            onChange={() => handleListToggle(list.id)}
                            disabled={alreadyOnList}
                          />
                          <span className="custom-checkbox"></span>
                          <span className="list-checkbox-name">
                            {list.title}
                            {alreadyOnList && <span className="already-badge">Already added</span>}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="no-lists-message">
                  <p>You don't have any lists yet. Create a list first to save places.</p>
                  <button
                    type="button"
                    className="btn-create-list"
                    onClick={() => navigate('/lists/new')}
                  >
                    Create a List
                  </button>
                </div>
              )}
            </>
          )}

          {error && (
            <div className="error-message">{error}</div>
          )}

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={handleClose}>
              Cancel
            </button>
            {placeSelected && lists.length > 0 && (
              <button
                type="submit"
                className="btn-submit"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Place'}
              </button>
            )}
          </div>
        </form>
      </div>

      <LimitReachedModal
        isOpen={limitModalOpen}
        onClose={() => setLimitModalOpen(false)}
        type="places"
        limit={limitModalLimit}
      />
    </div>
  );
}

export default AddPlaceFromSearch;
